import React from 'react';

/**
 * Book3D — a pure-CSS 3D book mockup (no images required).
 * Renders a cover with a spine, page-block edge, sheen and ground shadow.
 * Tilts toward the reader on hover. Used in the Cover Studio gallery/teaser.
 *
 * props:
 *   title, author   — text on the cover (ignored when coverImage is set — the image already has its own design)
 *   palette         — { bg (CSS background), text, accent } — used only when coverImage is not set
 *   coverImage      — optional real cover image URL; renders the actual designed cover on the 3D book instead of a flat gradient+text mockup
 *   width           — cover width in px (height derived 1.5x)
 *   angle           — resting rotateY degrees (default -24)
 */
const Book3D = ({ title = 'Your Title', author = 'Your Name', palette = {}, coverImage = null, width = 180, angle = -24 }) => {
  const h = Math.round(width * 1.5);
  const depth = Math.max(10, Math.round(width * 0.07));
  const bg = palette.bg || 'linear-gradient(135deg,#1a1a2e,#16213e)';
  const text = palette.text || '#f4f1ea';
  const accent = palette.accent || '#c9a227';

  return (
    <div className="inline-block" style={{ perspective: '1400px' }}>
      <div
        className="relative transition-transform duration-500 ease-out hover:[transform:rotateY(-8deg)_translateY(-4px)]"
        style={{ width, height: h, transformStyle: 'preserve-3d', transform: `rotateY(${angle}deg)` }}
      >
        {/* page block (right edge) */}
        <div style={{
          position: 'absolute', top: 4, right: -depth, width: depth, height: h - 8,
          background: 'repeating-linear-gradient(0deg,#fdfcf9 0,#fdfcf9 1px,#dcd6c8 2px)',
          transform: `rotateY(90deg) translateX(${depth / 2}px)`, transformOrigin: 'right center',
          borderRadius: '0 2px 2px 0',
        }} />
        {/* cover face */}
        <div style={{
          position: 'absolute', inset: 0,
          background: coverImage ? `url(${coverImage}) center/cover no-repeat` : bg,
          color: text,
          borderRadius: '3px 7px 7px 3px', overflow: 'hidden',
          boxShadow: '0 24px 45px -14px rgba(0,0,0,.55), 0 6px 14px -8px rgba(0,0,0,.4)',
        }}>
          {/* spine shadow (left) */}
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '16%', background: 'linear-gradient(90deg, rgba(0,0,0,.42), rgba(0,0,0,.06) 60%, rgba(255,255,255,.05))' }} />
          {/* sheen */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, rgba(255,255,255,.16), transparent 38%)' }} />
          {/* title block — only rendered for the flat-palette mockup mode; real cover images already carry their own design */}
          {!coverImage && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" style={{ paddingLeft: '14%' }}>
              <div style={{ width: 34, height: 2, background: accent, marginBottom: 10 }} />
              <div style={{ fontFamily: 'Georgia, serif', fontWeight: 700, lineHeight: 1.1, fontSize: width * 0.11, letterSpacing: '.5px' }}>{title}</div>
              <div style={{ marginTop: 10, fontSize: width * 0.05, opacity: .8, letterSpacing: '2px', textTransform: 'uppercase' }}>{author}</div>
            </div>
          )}
        </div>
      </div>
      {/* ground shadow */}
      <div className="mx-auto" style={{ width: width * 0.8, height: 14, marginTop: 10, background: 'radial-gradient(ellipse at center, rgba(0,0,0,.28), transparent 70%)', filter: 'blur(2px)' }} />
    </div>
  );
};

export default Book3D;
